import { useEffect, useMemo, useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { fetchSalesForSelect, type SaleOption } from '@/services/lookup';
import { fetchVariants, type VariantOption } from '@/services/variants';
import { createExchange } from '@/services/exchanges';

interface ExchangeModalProps {
  onClose: () => void;
  onSaved: () => void;
}

export default function ExchangeModal({ onClose, onSaved }: ExchangeModalProps) {
  const [sales, setSales] = useState<SaleOption[]>([]);
  const [variants, setVariants] = useState<VariantOption[]>([]);
  const [saleId, setSaleId] = useState('');
  const [exchangeType, setExchangeType] = useState<'size_change' | 'product_change'>('size_change');
  const [outVariantId, setOutVariantId] = useState('');
  const [inVariantId, setInVariantId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSalesForSelect().then((r) => {
      setSales(r);
      if (r[0]) setSaleId(r[0].id);
    });
    fetchVariants().then((r) => setVariants(r.data));
  }, []);

  const priceDifference = useMemo(() => {
    const outV = variants.find((x) => x.id === outVariantId);
    const inV = variants.find((x) => x.id === inVariantId);
    return outV && inV ? (inV.retailPrice - outV.retailPrice) * (Number(quantity) || 0) : 0;
  }, [outVariantId, inVariantId, quantity, variants]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!saleId) {
      setError('Selecciona la venta original.');
      return;
    }
    if (!outVariantId || !inVariantId) {
      setError('Selecciona el producto saliente y el entrante.');
      return;
    }
    const sale = sales.find((s) => s.id === saleId);
    if (!sale?.customerId) {
      setError('No se pudo identificar al cliente de la venta.');
      return;
    }

    setSaving(true);
    const res = await createExchange({
      saleId,
      customerId: sale.customerId,
      exchangeType,
      outVariantId,
      inVariantId,
      quantity: Math.max(1, Number(quantity) || 1),
      priceDifference,
      reason,
    });
    setSaving(false);

    if (!res.success) {
      setError(res.error || 'No se pudo crear el cambio.');
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <Modal title="Nuevo Cambio" subtitle="Registra un cambio de talla o producto." onClose={onClose} maxWidth="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Venta original *</label>
            <select className={inputCls} value={saleId} onChange={(e) => setSaleId(e.target.value)}>
              {sales.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.saleNumber} · {s.customerName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Tipo de cambio</label>
            <select className={inputCls} value={exchangeType} onChange={(e) => setExchangeType(e.target.value as 'size_change' | 'product_change')}>
              <option value="size_change">Cambio de talla</option>
              <option value="product_change">Cambio de producto</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Producto saliente *</label>
            <select className={inputCls} value={outVariantId} onChange={(e) => setOutVariantId(e.target.value)}>
              <option value="">Selecciona...</option>
              {variants.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.productName} · {v.sizeName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Producto entrante *</label>
            <select className={inputCls} value={inVariantId} onChange={(e) => setInVariantId(e.target.value)}>
              <option value="">Selecciona...</option>
              {variants.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.productName} · {v.sizeName}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Cantidad</label>
            <input type="number" min="1" className={inputCls} value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Diferencia de precio</label>
            <input className={inputCls} value={priceDifference === 0 ? '' : (priceDifference > 0 ? `+$${priceDifference.toLocaleString()}` : `-$${Math.abs(priceDifference).toLocaleString()}`)} readOnly />
          </div>
        </div>

        <div>
          <label className={labelCls}>Motivo</label>
          <textarea className={`${inputCls} resize-none`} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Talla no le queda, prefiere otro color, etc." />
        </div>

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? (<><i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...</>) : (<><i className="ri-save-line mr-1.5"></i>Crear cambio</>)}
          </button>
        </div>
      </form>
    </Modal>
  );
}